import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

type Brand = { id: string; name: string };
type User = { 
  id: string; 
  email: string; 
  firstName?: string; 
  lastName?: string; 
  isActive: boolean; 
  createdAt: string; 
  brandId?: string; 
  Brand?: Brand;
  roles?: string[];
};

export default function AdminUsers() {
  const [items, setItems] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', password: '', role: 'user', brandId: '' });
  const [editForm, setEditForm] = useState({ id: '', email: '', firstName: '', lastName: '', role: 'user', brandId: '' });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [showEditCreateBrand, setShowEditCreateBrand] = useState(false);
  const [newBrandForm, setNewBrandForm] = useState({ name: '', description: '' });
  const [creatingBrand, setCreatingBrand] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  async function load() {
    const res = await fetch(`/api/admin/users?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`, { credentials: 'include' });
    if (res.status === 403) {
      setItems([]);
      setTotal(0);
      return;
    }
    const data = await res.json();
    setItems(data.items || []);
    setTotal(data.total || 0);
  }

  async function loadBrands() {
    const res = await fetch('/api/admin/brands', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setBrands(data);
    }
  }

  async function loadPermissions() {
    const res = await fetch('/api/admin/users/me/permissions', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setPermissions(data.permissions || []);
    }
  }

  async function loadCurrentUser() {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setCurrentUserId(data.id);
    }
  }

  useEffect(() => { 
    load(); 
    loadBrands(); 
    loadPermissions();
    loadCurrentUser();
  }, [q, page, pageSize]);

  const canManageAdmins = permissions.includes('users:manage-admins');
  
  function isAdminUser(user: User): boolean {
    return user.roles?.some(r => r === 'admin' || r === 'superadmin') || false;
  }

  function canEditUser(user: User): boolean {
    if (user.id === currentUserId) return true; // Can always edit self
    if (isAdminUser(user) && !canManageAdmins) return false; // Cannot edit other admins
    return true;
  }

  function canDeleteUser(user: User): boolean {
    if (user.id === currentUserId) return false; // Cannot delete self
    if (!canManageAdmins) return false; // Only super admins can delete users
    return true;
  }

  async function createUser() {
    if (!form.firstName.trim()) {
      alert('First name is required');
      return;
    }
    if (!form.email.trim()) {
      alert('Email is required');
      return;
    }
    if (!form.password || form.password.length < 8) {
      alert('Password is required and must be at least 8 characters');
      return;
    }
    const body = { 
      ...form, 
      roles: [form.role], // Send as array with single role
      brandId: form.brandId || null
    };
    const res = await fetch('/api/admin/users', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' });
    if (res.ok) { 
      setOpen(false); 
      setForm({ email: '', firstName: '', lastName: '', password: '', role: 'user', brandId: '' }); 
      load(); 
    } else {
      const error = await res.json();
      let errorMessage = error.error || 'Failed to create user';
      if (error.details && Array.isArray(error.details)) {
        const validationErrors = error.details.map((d: any) => `${d.path.join('.')}: ${d.message}`).join('\n');
        errorMessage = `Validation error:\n${validationErrors}`;
      }
      alert(errorMessage);
      console.error('Create user error:', error);
    }
  }

  async function updateUser() {
    if (!selectedUser) return;
    const body = {
      email: editForm.email,
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      roles: [editForm.role], // Send as array with single role
      brandId: editForm.brandId || null
    };
    const res = await fetch(`/api/admin/users/${selectedUser.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' });
    if (res.ok) { setOpenEdit(false); setSelectedUser(null); load(); }
  }

  function openEditDialog(user: User) {
    setSelectedUser(user);
    // Get the first non-brand role, or default to 'user'
    const primaryRole = user.roles?.find(r => r !== 'brand') || 'user';
    setEditForm({
      id: user.id,
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: primaryRole,
      brandId: user.brandId || ''
    });
    setOpenEdit(true);
  }

  async function toggleActive(user: User) {
    await fetch(`/api/admin/users/${user.id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ isActive: !user.isActive }), credentials: 'include' });
    load();
  }

  async function removeUser(user: User) {
    if (!window.confirm(`Are you sure you want to delete user "${user.email}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      
      if (!res.ok) {
        alert(data.error || 'Failed to delete user');
        return;
      }
      
      load();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('An error occurred while deleting the user. Please try again.');
    }
  }

  async function resetPassword(user: User) {
    await fetch(`/api/admin/users/${user.id}/reset`, { method: 'POST', credentials: 'include' });
    alert('If SMTP is configured, a reset email was sent; otherwise the link was logged on server.');
  }

  async function createBrandInline(forEdit: boolean = false) {
    if (!newBrandForm.name.trim()) {
      alert('Brand name is required');
      return;
    }

    setCreatingBrand(true);
    try {
      const res = await fetch('/api/admin/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newBrandForm.name,
          description: newBrandForm.description || null,
          isActive: true
        })
      });

      if (res.ok) {
        const newBrand = await res.json();
        // Reload brands list
        await loadBrands();
        // Auto-select the new brand
        if (forEdit) {
          setEditForm({ ...editForm, brandId: newBrand.id });
          setShowEditCreateBrand(false);
        } else {
          setForm({ ...form, brandId: newBrand.id });
          setShowCreateBrand(false);
        }
        // Reset form
        setNewBrandForm({ name: '', description: '' });
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to create brand');
      }
    } catch (error) {
      console.error('Failed to create brand:', error);
      alert('Failed to create brand');
    } finally {
      setCreatingBrand(false);
    }
  }

  return (
    <div className="h-full">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Users</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>New User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create User</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input 
                    id="email" 
                    type="email"
                    value={form.email} 
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName">First name *</Label>
                    <Input id="firstName" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={form.password} 
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                    required
                  />
                  {form.password && form.password.length < 8 && (
                    <p className="text-xs text-red-600 mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin" disabled={!canManageAdmins}>
                        Admin {!canManageAdmins && '(Super Admin only)'}
                      </SelectItem>
                      {canManageAdmins && (
                        <SelectItem value="superadmin">Super Admin</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="brand">Brand (optional)</Label>
                  <Select value={form.brandId || 'none'} onValueChange={(value) => setForm({ ...form, brandId: value === 'none' ? '' : value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="No brand" />
                    </SelectTrigger>
                    <SelectContent className="z-[2000]">
                      <SelectItem value="none">No brand</SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!showCreateBrand ? (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="px-0 mt-1 h-auto text-blue-600"
                      onClick={() => setShowCreateBrand(true)}
                    >
                      + Create New Brand
                    </Button>
                  ) : (
                    <div className="mt-3 p-3 border rounded-md bg-slate-50 space-y-2">
                      <div>
                        <Label htmlFor="new-brand-name" className="text-xs">Brand Name *</Label>
                        <Input
                          id="new-brand-name"
                          value={newBrandForm.name}
                          onChange={(e) => setNewBrandForm({ ...newBrandForm, name: e.target.value })}
                          placeholder="Enter brand name"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-brand-desc" className="text-xs">Description (optional)</Label>
                        <Input
                          id="new-brand-desc"
                          value={newBrandForm.description}
                          onChange={(e) => setNewBrandForm({ ...newBrandForm, description: e.target.value })}
                          placeholder="Enter description"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => createBrandInline(false)}
                          disabled={creatingBrand}
                        >
                          {creatingBrand ? 'Creating...' : 'Create Brand'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowCreateBrand(false);
                            setNewBrandForm({ name: '', description: '' });
                          }}
                          disabled={creatingBrand}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <Button type="button" onClick={createUser}>Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mb-4">
          <Input placeholder="Search email..." value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 font-semibold">Email</th>
                <th className="text-left p-2 font-semibold">Name</th>
                <th className="text-left p-2 font-semibold">Roles</th>
                <th className="text-left p-2 font-semibold">Brand</th>
                <th className="text-left p-2 font-semibold">Active</th>
                <th className="text-left p-2 font-semibold">Created</th>
                <th className="text-left p-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const userIsAdmin = isAdminUser(u);
                const canEdit = canEditUser(u);
                const canDelete = canDeleteUser(u);
                const isCurrentUser = u.id === currentUserId;
                
                return (
                  <tr key={u.id} className={`border-t ${userIsAdmin && !canEdit ? 'bg-gray-50' : ''}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        {u.email}
                        {isCurrentUser && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">You</span>
                        )}
                      </div>
                    </td>
                    <td className="p-2">{`${u.firstName || ''} ${u.lastName || ''}`.trim()}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {u.roles?.map(role => (
                          <span 
                            key={role}
                            className={`text-xs px-2 py-0.5 rounded ${
                              role === 'superadmin' ? 'bg-purple-100 text-purple-700' :
                              role === 'admin' ? 'bg-indigo-100 text-indigo-700' :
                              role === 'brand' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {role === 'superadmin' ? 'Super Admin' : 
                             role === 'admin' ? 'Admin' :
                             role === 'brand' ? 'Brand' :
                             role.charAt(0).toUpperCase() + role.slice(1)}
                          </span>
                        ))}
                        {(!u.roles || u.roles.length === 0) && <span className="text-gray-400">-</span>}
                      </div>
                    </td>
                    <td className="p-2">{u.Brand ? u.Brand.name : '-'}</td>
                    <td className="p-2">{u.isActive ? 'Yes' : 'No'}</td>
                    <td className="p-2">
                      {u.createdAt && u.createdAt !== '0' 
                        ? new Date(u.createdAt).toLocaleString() 
                        : '-'}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditDialog(u)}
                          disabled={!canEdit}
                          title={!canEdit ? 'Only Super Admins can edit other admin users' : ''}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => toggleActive(u)}
                          disabled={isCurrentUser || (userIsAdmin && !canManageAdmins)}
                          title={isCurrentUser ? 'You cannot change your own status' : (!canEdit ? 'Only Super Admins can manage admin users' : '')}
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => removeUser(u)}
                          disabled={!canDelete}
                          title={isCurrentUser ? 'You cannot delete your own account' : (!canDelete ? 'Only Super Admins can delete users' : '')}
                        >
                          Delete
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => resetPassword(u)}
                          disabled={userIsAdmin && !canEdit && !isCurrentUser}
                          title={!canEdit && !isCurrentUser ? 'Only Super Admins can reset admin passwords' : ''}
                        >
                          Reset
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div>Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} total</div>
          <div className="flex items-center gap-2">
            <button disabled={page<=1} onClick={() => setPage(page-1)} className="px-2 py-1 border rounded">Prev</button>
            <button disabled={page>=Math.max(1, Math.ceil(total / pageSize))} onClick={() => setPage(page+1)} className="px-2 py-1 border rounded">Next</button>
            <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value,10))} className="border rounded p-1">
              {[10,20,50].map(s => <option key={s} value={s}>{s}/page</option>)}
            </select>
          </div>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={openEdit} onOpenChange={setOpenEdit}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-firstName">First name</Label>
                  <Input id="edit-firstName" value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="edit-lastName">Last name</Label>
                  <Input id="edit-lastName" value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select 
                  value={editForm.role} 
                  onValueChange={(value) => setEditForm({ ...editForm, role: value })}
                  disabled={selectedUser?.id === currentUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin" disabled={!canManageAdmins}>
                      Admin {!canManageAdmins && '(Super Admin only)'}
                    </SelectItem>
                    {canManageAdmins && (
                      <SelectItem value="superadmin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedUser?.id === currentUserId && (
                  <p className="text-xs text-gray-500 mt-1">You cannot change your own role</p>
                )}
              </div>
              <div>
                <Label htmlFor="edit-brand">Brand (optional)</Label>
                <Select value={editForm.brandId || 'none'} onValueChange={(value) => setEditForm({ ...editForm, brandId: value === 'none' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="No brand" />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="none">No brand</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!showEditCreateBrand ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="px-0 mt-1 h-auto text-blue-600"
                    onClick={() => setShowEditCreateBrand(true)}
                  >
                    + Create New Brand
                  </Button>
                ) : (
                  <div className="mt-3 p-3 border rounded-md bg-slate-50 space-y-2">
                    <div>
                      <Label htmlFor="edit-new-brand-name" className="text-xs">Brand Name *</Label>
                      <Input
                        id="edit-new-brand-name"
                        value={newBrandForm.name}
                        onChange={(e) => setNewBrandForm({ ...newBrandForm, name: e.target.value })}
                        placeholder="Enter brand name"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-new-brand-desc" className="text-xs">Description (optional)</Label>
                      <Input
                        id="edit-new-brand-desc"
                        value={newBrandForm.description}
                        onChange={(e) => setNewBrandForm({ ...newBrandForm, description: e.target.value })}
                        placeholder="Enter description"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => createBrandInline(true)}
                        disabled={creatingBrand}
                      >
                        {creatingBrand ? 'Creating...' : 'Create Brand'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowEditCreateBrand(false);
                          setNewBrandForm({ name: '', description: '' });
                        }}
                        disabled={creatingBrand}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={updateUser}>Save Changes</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
